import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth, useCart } from '@/contexts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { getLocalizedField, getImageUrl, FallbackStrategy, hasTranslation } from '@/utils/helpers';
import ShareButton from '@/components/common/ShareButton';
import { ArrowLeft, Package, Star, ShoppingCart, Heart, Plus, Minus, ExternalLink, Palette, User } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductDetailPage = () => {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [isAdding, setIsAdding] = useState(false);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (productId) {
      axios.get(`${API}/products/${productId}`)
        .then(res => setProduct(res.data))
        .catch(err => {
          console.error('Failed to fetch product', err);
          toast.error(t('products.notFound'));
          navigate('/');
        })
        .finally(() => setLoading(false));
    }
  }, [productId, navigate, t]);

  const handleAddToCart = async () => {
    setIsAdding(true);
    for (let i = 0; i < quantity; i++) {
      await addToCart(product.id);
    }
    setIsAdding(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-amber-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-green-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-amber-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-amber-50 flex items-center justify-center">
        <Card className="max-w-md text-center p-8">
          <Package className="w-16 h-16 mx-auto text-amber-500 mb-4" />
          <h2 className="text-xl font-bold mb-2">{t('products.notFound')}</h2>
          <p className="text-amber-600 mb-4">{t('products.notFoundDesc')}</p>
          <Button onClick={() => navigate('/')} className="bg-green-600 hover:bg-green-700">{t('common.backToHome')}</Button>
        </Card>
      </div>
    );
  }

  const productName = getLocalizedField(product, 'name', i18n.language) || product.name;
  // 使用新的回退策略：描述翻译缺失时显示占位符
  const productDesc = getLocalizedField(product, 'description', i18n.language, {
    fallback: FallbackStrategy.SHOW_PLACEHOLDER,
    placeholder: t('common.translationMissing')
  });
  const hasDescTranslation = hasTranslation(product, 'description', i18n.language);

  return (
    <div className="min-h-screen bg-amber-50 pt-16" data-testid="product-detail-page">
      <div className="container mx-auto px-4 py-8">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" /> {t('common.back')}
        </Button>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Product Images */}
          <div>
            <div className="relative rounded-2xl overflow-hidden mb-4">
              <img src={getImageUrl(product.image_url)} alt={productName} className="w-full h-[400px] object-cover" />
              {product.badge && (
                <Badge className={`absolute top-4 left-4 ${product.badge === 'sale' ? 'bg-red-500' : product.badge === '热销' ? 'bg-orange-500' : 'bg-blue-500'}`}>
                  {t(`tags.${product.badge}`, product.badge)}
                </Badge>
              )}
            </div>
            {product.images && product.images.length > 0 && (
              <div className="grid grid-cols-4 gap-2">
                {product.images.slice(0, 4).map((img, idx) => (
                  <img key={idx} src={getImageUrl(img)} alt={`${productName} ${idx + 1}`} className="w-full h-20 object-cover rounded cursor-pointer hover:opacity-80" />
                ))}
              </div>
            )}
            {product.video_url && (
              <div className="mt-4">
                <video src={product.video_url} controls className="w-full rounded-lg" />
              </div>
            )}
          </div>

          {/* Product Info */}
          <div>
            <div className="flex flex-wrap gap-2 mb-4">
              {product.tags.map((tag) => (
                <Badge key={tag} variant="outline" className="border-green-200 text-green-700">{t(`tags.${tag}`, tag)}</Badge>
              ))}
            </div>
            <h1 className="text-3xl font-bold text-amber-900 mb-4">{productName}</h1>
            {product.origin && <p className="text-amber-600 mb-2">{t('products.origin')}：{product.origin}</p>}
            {product.brand && <p className="text-amber-600 mb-4">{t('products.brand')}：{product.brand}</p>}
            
            <div className="flex items-center gap-2 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className={`w-5 h-5 ${i < Math.floor(product.rating) ? 'text-yellow-400 fill-yellow-400' : 'text-amber-400'}`} />
              ))}
              <span className="text-amber-600">({product.review_count} {t('products.reviews')})</span>
            </div>

            <div className="flex items-baseline gap-4 mb-6">
              <span className="text-4xl font-bold text-green-600">¥{product.price.toFixed(2)}</span>
              {product.original_price && (
                <span className="text-xl text-amber-500 line-through">¥{product.original_price.toFixed(2)}</span>
              )}
            </div>

            <p className={`text-amber-700 mb-6 ${!hasDescTranslation && i18n.language !== 'zh' ? 'italic opacity-70' : ''}`}>
              {productDesc}
            </p>

            {/* 根据产品类型显示不同内容 */}
            {product.product_type === 'external' ? (
              /* 外购产品 - 显示外部链接按钮 */
              <div className="flex gap-4">
                <Button 
                  size="lg" 
                  className="flex-1 bg-blue-600 hover:bg-blue-700" 
                  onClick={() => product.external_url && window.open(product.external_url, '_blank', 'noopener,noreferrer')}
                  data-testid="external-link-btn"
                >
                  <ExternalLink className="w-5 h-5 mr-2" />
                  {t('products.learnMore', '进一步了解')}
                </Button>
                <Button size="lg" variant="outline">
                  <Heart className="w-5 h-5" />
                </Button>
                <ShareButton
                  url={`/product/${productId}`}
                  title={productName}
                  description={productDesc?.substring(0, 200) || ''}
                  image={product.image_url}
                  variant="outline"
                  size="lg"
                />
              </div>
            ) : (
              /* 自营产品 - 显示购物车功能 */
              <>
                <div className="flex items-center gap-4 mb-6">
                  <span className="text-amber-600">{t('products.quantity')}</span>
                  <div className="flex items-center gap-2">
                    <Button size="icon" variant="outline" onClick={() => setQuantity(Math.max(1, quantity - 1))}>
                      <Minus className="w-4 h-4" />
                    </Button>
                    <span className="w-12 text-center font-medium">{quantity}</span>
                    <Button size="icon" variant="outline" onClick={() => setQuantity(quantity + 1)}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <span className="text-amber-600">{t('products.stock')} {product.stock} {t('products.stockUnit')}</span>
                </div>

                <div className="flex gap-4">
                  <Button size="lg" className="flex-1 bg-green-600 hover:bg-green-700" onClick={handleAddToCart} disabled={isAdding}>
                    <ShoppingCart className="w-5 h-5 mr-2" />
                    {isAdding ? t('products.adding') : t('products.addToCart')}
                  </Button>
                  <Button size="lg" variant="outline">
                    <Heart className="w-5 h-5" />
                  </Button>
                  <ShareButton
                    url={`/product/${productId}`}
                    title={productName}
                    description={productDesc?.substring(0, 200) || ''}
                    image={product.image_url}
                    variant="outline"
                    size="lg"
                  />
                </div>
              </>
            )}

            {product.detail_content && (
              <Card className="mt-8">
                <CardHeader>
                  <CardTitle>{t('products.productDetail')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-amber-700 whitespace-pre-wrap">{product.detail_content}</p>
                </CardContent>
              </Card>
            )}

            {/* 关联作品展示 - 显示印在这个产品上的作品 */}
            {product.related_artworks && product.related_artworks.length > 0 && (
              <Card className="mt-8 border-amber-200 bg-amber-50/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-amber-800">
                    <Palette className="w-5 h-5" />
                    {t('products.artworkOnProduct', '印制作品')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {product.related_artworks.map((artwork) => (
                      <div 
                        key={artwork.id}
                        className="flex gap-4 p-4 bg-white rounded-lg cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => navigate(`/artwork/${artwork.id}`)}
                      >
                        <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden">
                          <img loading="lazy"
                            src={getImageUrl(artwork.image_url)} 
                            alt={getLocalizedField(artwork, 'title', i18n.language)}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-amber-900 truncate">
                            {getLocalizedField(artwork, 'title', i18n.language)}
                          </h4>
                          <p className="text-sm text-amber-600 flex items-center gap-1 mt-1">
                            <User className="w-4 h-4" />
                            {artwork.creator?.creator_name || getLocalizedField(artwork, 'artist', i18n.language)}
                          </p>
                          {artwork.creator && (
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 h-auto text-amber-600 mt-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/creator/${artwork.creator.id}`);
                              }}
                            >
                              {t('products.viewCreator', '查看创作者主页')} →
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-amber-700 mt-4">
                    {t('products.artworkNote', '购买此产品即支持原创艺术家，艺术家将获得销售分成。')}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
